import type { BriefAgentChoice } from "@/hooks/api/use-campaign-briefs";
import type { RateCardItem } from "@/hooks/api/use-rate-card";

import { languageLabel, regionLabel } from "./constants";

/** The rate card lines a request is priced from besides the optional services. Mirrors `RATE_CARD_PRICING` in the API. */
export const PRICING = {
	/** The main agent and the first market. Every request has it. */
	setup: "campaign_setup",
	marketEnglish: "additional_region_english",
	marketLocalised: "additional_region_multilingual",
	extraAgent: "multi_agent",
} as const;

export interface EstimateLine {
	/** One per thing the client added, so the panel can point at the line that just changed. */
	key: string;
	label: string;
	detail: string;
	/** Excluding GST, or null where the card shows a note instead of a price. */
	amount: number | null;
	priceNote: string | null;
}

export interface Estimate {
	currency: string;
	lines: EstimateLine[];
	/** The priced lines added up. */
	total: number;
}

export interface EstimateInput {
	/** An existing agent is already set up, so there is no setup fee. Null until the client picks. */
	agentChoice: BriefAgentChoice | null;
	markets: { region: string; language: string }[];
	extraAgents: string[];
	/** Rate card codes of the optional services picked. */
	addOns: string[];
}

export const isEnglish = (language: string) => language.split("-")[0].toLowerCase() === "en";

/** What a further market costs in this language: the English rate, or the localised one. */
export const marketCode = (language: string) => (isEnglish(language) ? PRICING.marketEnglish : PRICING.marketLocalised);

/** The card's price for a line, or null where it has none. */
export const amountOf = (card: RateCardItem[], code: string) => {
	const amount = card.find((item) => item.code === code)?.amount;
	return amount === null || amount === undefined ? null : Number(amount);
};

/**
 * The request priced from the rate card, the same way the API prices it when it is saved: setup
 * covers the main agent and the first market, and is not charged for an existing agent; each
 * further market is charged at the English or the localised rate by its language; each extra agent
 * at the multi-agent rate; then the services.
 */
export function estimateRequest(
	{ agentChoice, markets, extraAgents, addOns }: EstimateInput,
	card: RateCardItem[]
): Estimate {
	const priced = (key: string, code: string, label: string, detail: string): EstimateLine => {
		const item = card.find((line) => line.code === code);
		return {
			key,
			label,
			detail,
			amount: item?.amount === null || item?.amount === undefined ? null : Number(item.amount),
			priceNote: item ? item.priceNote : "Price on request",
		};
	};

	const setup = (first: { region: string }): EstimateLine =>
		agentChoice === "existing"
			? {
					key: "setup",
					label: "Campaign setup",
					detail: `Your existing agent and ${regionLabel(first.region)}, no setup fee`,
					amount: 0,
					priceNote: null,
				}
			: priced("setup", PRICING.setup, "Campaign setup", `Main agent and ${regionLabel(first.region)}`);

	const [first, ...further] = markets;
	const lines: EstimateLine[] = [
		...(first ? [setup(first)] : []),
		...further.map(({ region, language }) =>
			priced(
				`market:${region}`,
				marketCode(language),
				`${regionLabel(region)} · ${languageLabel(language)}`,
				isEnglish(language) ? "Further market, English" : "Further market, localised"
			)
		),
		...extraAgents.map((label, index) =>
			priced(`agent:${index}`, PRICING.extraAgent, label.trim() || "Extra agent", "Extra agent")
		),
		...addOns.map((code) => {
			const item = card.find((line) => line.code === code);
			return priced(
				`addon:${code}`,
				code,
				item?.name ?? code,
				item?.unit ? `Optional · ${item.unit}` : "Optional service"
			);
		}),
	];

	const cents = lines.reduce((sum, { amount }) => sum + (amount === null ? 0 : Math.round(amount * 100)), 0);
	return { currency: card.find((item) => item.code === PRICING.setup)?.currency ?? "SGD", lines, total: cents / 100 };
}
